'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import briskOliveLogo from '../assets/Brisk-Olive-Logo.png';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box, Collapse, Typography, } from '@mui/material';
import { Dashboard as DashboardIcon, Home as HomeIcon, People as PeopleIcon, BusinessCenter as BusinessCenterIcon, ExitToApp as ExitToAppIcon, AccountCircle as AccountCircleIcon, Settings as SettingsIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, School as SchoolIcon, Event as EventIcon, Domain as CorporateFareIcon, Apartment as ApartmentIcon, AccessTime as AccessTimeIcon, RequestPage as RequestPageIcon, CheckCircle as CheckCircleIcon, MonetizationOn as MonetizationOnIcon, ReportProblem as ReportProblemIcon, } from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';
import { usePageVisibility } from '../contexts/PageVisibilityContext';
const drawerWidth = 220;
const BRAND_BLUE = '#4A6427';
// ─── Component ─────────────────────────────────
export default function Sidebar() {
    const location = useLocation();
    const [openPMS, setOpenPMS] = useState(false);
    const isActive = (path) => {
        const current = location.pathname + location.search;
        return current === path || current.startsWith(path + '&');
    };
    // Auto-expand parent menus if any sub-item is active
    useEffect(() => {
        // Check if any PMS sub-item is active
        const pmsActive = [
            '/pms?tab=kpi',
            '/pms?tab=hygiene',
            '/pms?tab=growth',
            '/pms?tab=summary'
        ].some(path => isActive(path));
        setOpenPMS(pmsActive);
    }, [location.pathname, location.search]);
    const { canViewKey } = usePageVisibility();
    const menuItems = [
        { to: '/', text: 'Home', icon: _jsx(HomeIcon, {}) },
        { to: '/hr-dashboard', text: 'Dashboard', icon: _jsx(DashboardIcon, {}), pageKey: 'dashboard' },
        { to: '/recruitment', text: 'Recruitment', icon: _jsx(RequestPageIcon, {}), pageKey: 'recruitment.dashboard' },
        { to: '/onboarding/dashboard', text: 'Onboarding', icon: _jsx(BusinessCenterIcon, {}), pageKey: 'onboarding.dashboard' },
        { to: '/employees', text: 'Employees List', icon: _jsx(PeopleIcon, {}), pageKey: 'employees' },
        { to: '/attendance?tab=out-of-office', text: 'Attendance & Leave', icon: _jsx(AccessTimeIcon, {}), pageKey: 'hygieneFactors' },
        { to: '/confirmations', text: 'Confirmations', icon: _jsx(CheckCircleIcon, {}), pageKey: 'confirmations' },
        { to: '/salary-revision', text: 'Salary Revision', icon: _jsx(MonetizationOnIcon, {}), pageKey: 'salaryRevision' },
        { to: '/training-page?tab=HR', text: 'Trainings', icon: _jsx(SchoolIcon, {}), pageKey: 'trainings' },
        { to: '/outing?tab=HR', text: 'Outings / Events', icon: _jsx(EventIcon, {}), pageKey: 'outings' },
        { to: '/escalations', text: 'Escalations / Grievances', icon: _jsx(ReportProblemIcon, {}), pageKey: 'escalations' },
        { to: '/exits', text: 'Exit Management', icon: _jsx(ExitToAppIcon, {}), pageKey: 'exit.dashboard' },
        { to: '/company-orientation', text: 'Company Orientation', icon: _jsx(CorporateFareIcon, {}), pageKey: 'companyOrientation' },
        { to: '/dept-orientation', text: 'Department Orientation', icon: _jsx(ApartmentIcon, {}), pageKey: 'deptOrientation' },
        { to: '/dept-designation-master', text: 'Dept & Designation Master', icon: _jsx(BusinessCenterIcon, {}), pageKey: 'deptDesignationMaster' },
        // { to: '/salary-sheet', text: 'Salary Sheet', icon: <PaymentsIcon /> },
        // PMS — commented out per request, not deleted (restore by uncommenting).
        // {
        //   text: 'PMS',
        //   icon: <TrendingUpIcon />,
        //   onClick: () => setOpenPMS(p => !p),
        //   open: openPMS,
        //   pageKey: 'pms',
        //   subItems: [
        //     { to: '/pms?tab=kpi', text: 'KPI & Targets', icon: <KpiIcon />, pageKey: 'pms.kpi' },
        //     { to: '/pms?tab=hygiene', text: 'Attendance', icon: <HygieneIcon />, pageKey: 'pms.hygiene' },
        //     { to: '/pms?tab=growth', text: 'Growth', icon: <GrowthIcon />, pageKey: 'pms.growth' },
        //     { to: '/pms?tab=summary', text: 'Final Performance', icon: <SummaryIcon />, pageKey: 'pms.summary' },
        //   ],
        // },
        // { to: '/checklist-delegation', text: 'Check List & Delegation', icon: <AssignmentTurnedInIcon /> },
        { to: '/profile', text: 'Profile', icon: _jsx(AccountCircleIcon, {}) },
        { to: '/configuration', text: 'Configuration', icon: _jsx(SettingsIcon, {}) },
    ];
    return (_jsxs(Drawer, { variant: "permanent", sx: {
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: BRAND_BLUE,
                borderRight: '1px solid rgba(255,255,255,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
        }, children: [_jsxs(Box, { sx: {
                    bgcolor: BRAND_BLUE,
                    color: 'white',
                    p: 2,
                    textAlign: 'center',
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                }, children: [_jsx(Box, { component: "img", src: briskOliveLogo, alt: "Brisk Olive", sx: {
                            height: 32,
                            mx: 'auto',
                            mb: 1.5,
                            display: 'block',
                            borderRadius: '6px',
                        } }), _jsx(Typography, { variant: "h6", fontWeight: 700, sx: {
                            fontSize: '1.1rem',
                            transition: 'all 0.3s ease',
                        }, children: "HR Portal" })] }), _jsx(List, { sx: {
                    px: 1,
                    mt: 2,
                    flex: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '4px',
                        transition: 'all 0.3s ease',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'rgba(255,255,255,0.18)',
                        borderRadius: '4px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.3)',
                        },
                    },
                }, children: menuItems.filter(item => !item.pageKey || canViewKey(item.pageKey)).map(item => {
                    if ('subItems' in item) {
                        const visibleSubItems = item.subItems.filter(sub => !sub.pageKey || canViewKey(sub.pageKey));
                        const isParentActive = visibleSubItems.some(sub => isActive(sub.to));
                        return (_jsxs(Box, { sx: { mb: 0.5 }, children: [_jsxs(ListItemButton, { onClick: item.onClick, sx: {
                                        borderRadius: '8px',
                                        bgcolor: isParentActive ? 'rgba(255,255,255,0.18)' : 'transparent',
                                        color: 'white',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        transform: 'translateX(0)',
                                        '&:hover': {
                                            bgcolor: isParentActive ? 'rgba(255,255,255,0.26)' : 'rgba(255,255,255,0.08)',
                                            transform: 'translateX(2px)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        },
                                        '&:active': {
                                            transform: 'translateX(1px)',
                                        },
                                    }, children: [_jsx(ListItemIcon, { sx: {
                                                color: 'inherit',
                                                minWidth: 34,
                                                transition: 'transform 0.3s ease',
                                                '.MuiListItemButton-root:hover &': {
                                                    transform: 'scale(1.1)',
                                                },
                                            }, children: item.icon }), _jsx(ListItemText, { primary: item.text, sx: {
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    transition: 'all 0.3s ease',
                                                },
                                            } }), item.open ? (_jsx(ExpandLessIcon, { sx: {
                                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            } })) : (_jsx(ExpandMoreIcon, { sx: {
                                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            } }))] }), _jsx(Collapse, { in: item.open, timeout: 300, easing: "cubic-bezier(0.4, 0, 0.2, 1)", children: _jsx(List, { disablePadding: true, children: visibleSubItems.map((sub, index) => {
                                            const active = isActive(sub.to);
                                            return (_jsxs(ListItemButton, { component: NavLink, to: sub.to, sx: {
                                                    pl: 6,
                                                    borderRadius: '6px',
                                                    bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                                                    color: 'white',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    transform: 'translateX(0)',
                                                    opacity: 1,
                                                    animation: active ? 'slideIn 0.3s ease' : 'none',
                                                    '&:hover': {
                                                        bgcolor: active ? 'rgba(255,255,255,0.26)' : 'rgba(255,255,255,0.08)',
                                                        transform: 'translateX(4px)',
                                                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                                    },
                                                    '&:active': {
                                                        transform: 'translateX(2px)',
                                                    },
                                                }, style: {
                                                    animationDelay: `${index * 50}ms`,
                                                }, children: [_jsx(ListItemIcon, { sx: {
                                                            color: 'inherit',
                                                            minWidth: 36,
                                                            fontSize: '0.85rem',
                                                            transition: 'transform 0.3s ease',
                                                            '.MuiListItemButton-root:hover &': {
                                                                transform: 'scale(1.1)',
                                                            },
                                                        }, children: sub.icon }), _jsx(ListItemText, { primary: sub.text, sx: {
                                                            '& .MuiListItemText-primary': {
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                transition: 'all 0.3s ease',
                                                            },
                                                        } })] }, sub.to));
                                        }) }) })] }, item.text));
                    }
                    // Pathname-only match — so an item linking to one tab of a
                    // multi-tab page (e.g. Trainings -> /training-page?tab=HR) stays
                    // highlighted while on any of that page's other tabs too.
                    const active = location.pathname === item.to.split('?')[0];
                    return (_jsxs(ListItemButton, { component: NavLink, to: item.to, sx: {
                            borderRadius: '8px',
                            bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                            color: 'white',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: 'translateX(0)',
                            '&:hover': {
                                bgcolor: active ? 'rgba(255,255,255,0.26)' : 'rgba(255,255,255,0.08)',
                                transform: 'translateX(2px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                            '&:active': {
                                transform: 'translateX(1px)',
                            },
                        }, children: [_jsx(ListItemIcon, { sx: {
                                    color: 'inherit',
                                    minWidth: 34,
                                    transition: 'transform 0.3s ease',
                                    '.MuiListItemButton-root:hover &': {
                                        transform: 'scale(1.1)',
                                    },
                                }, children: item.icon }), _jsx(ListItemText, { primary: item.text, sx: {
                                    '& .MuiListItemText-primary': {
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        transition: 'all 0.3s ease',
                                    },
                                } })] }, item.text));
                }) })] }));
}
